import React, { useEffect, useState } from "react";
import {
  FormControl,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import axios from "axios";
import { toLower } from "lodash";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import localize from "../../../common/localize";

export default function AddOrEditUser({
  passwordConfirmed,
  setPasswordConfirmed,
  modifyFullName,
  setModifyFullName,
  modifyPassword,
  setModifyPassword,
  modifyPersonal,
  setModifyPersonal,
}) {
  const { userModel, setUserModel, PERSONAL_API, config } = useGlobalContext();
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [personalData, setPersonalData] = useState(null);

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  useEffect(() => {
    getPersonal();
  }, []);

  useEffect(() => {
    if (personalData?.Data?.length > 0 && userModel) {
      const foundPerson = personalData.Data.find(
        (x) => x.id === userModel.PersonalId
      );
      if (foundPerson) {
        setUserModel({
          ...userModel,
          Personal: foundPerson,
        });
      }
    }
  }, [personalData]);

  const passwordRegex =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{6,20}$/;
  const [validPassword, setValidPassword] = useState(false);

  const handlePersonalCheckedChange = (e) => {
    setModifyPersonal(e.target.checked);
  };

  const handleUserNameCheckedChange = (e) => {
    setModifyFullName(e.target.checked);
  };

  const handleUserPasswordCheckedChange = (e) => {
    setModifyPassword(e.target.checked);
  };

  const handleChangeConfirmPassword = (e) => {
    setPasswordConfirmation(e.target.value);
    setPasswordConfirmed(userModel.UserPassword === e.target.value);
  };

  useEffect(() => {
    setPasswordConfirmed(validPassword && passwordConfirmed);
  }, [validPassword]);

  const validatePassword = (password) => {
    if (password) {
      const match = password.match(passwordRegex);
      if (match && match.length > 0) {
        setValidPassword(true);
        return;
      }
    }
    setValidPassword(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "UserName") {
      e.target.value = toLower(e.target.value);
    }

    if (e.target.name === "UserPassword") {
      validatePassword(e.target.value);
      setPasswordConfirmed(e.target.value === passwordConfirmation);
    }

    setUserModel({
      ...userModel,
      [e.target.name]: e.target.value,
    });
  };

  const handlePersonalChange = (personal) => {
    if (!personal) {
      setUserModel({
        ...userModel,
        Personal: undefined,
        PersonalId: 0,
      });
      return;
    }

    if (modifyFullName) {
      setUserModel({
        ...userModel,
        Personal: personal,
        PersonalId: personal.id,
        FullName: `${personal.SurName} ${personal.LastName}`,
      });
    } else {
      setUserModel({
        ...userModel,
        Personal: personal,
        PersonalId: personal.id,
      });
    }
  };

  useEffect(() => {
    if (personalData?.Data && userModel.UserId) {
      const personalObj = personalData.Data.find(
        (personalItem) => personalItem.id === userModel.PersonalId
      );
      setUserModel({ ...userModel, Personal: personalObj });
    }
  }, [userModel.UserId]);

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full mt-2 flex flex-col gap-3">
        <div className="flex w-full gap-2">
          {userModel.UserId && (
            <FormControlLabel
              control={<Checkbox onChange={handlePersonalCheckedChange} />}
            />
          )}

          <Combobox
            disabled={!!userModel.UserId && !modifyPersonal}
            id="Personal"
            handleValueChange={handlePersonalChange}
            fieldValue={userModel?.Personal ?? null}
            fieldOptions={personalData?.Data ?? []}
            fieldLabel={localize.personal_for_field_work}
            customOptionLabel={(option) =>
              `${option.Identification} - ${option.SurName} ${option.LastName}`
            }
          />
        </div>
        <div className="flex gap-2 w-full">
          {userModel.UserId && (
            <FormControlLabel
              control={<Checkbox onChange={handleUserNameCheckedChange} />}
            />
          )}

          <FormControl required fullWidth>
            <TextField
              required
              disabled={!!userModel.UserId && !modifyFullName}
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              fullWidth
              id="Name"
              name="FullName"
              label={localize.full_name}
              variant="outlined"
              onChange={handleChange}
              value={userModel.FullName}
            />
          </FormControl>
        </div>
        <div
          className="flex w-full"
          style={{ paddingLeft: userModel.UserId > 0 ? "55px" : "0px" }}
        >
          <FormControl required fullWidth>
            <TextField
              required
              style={{
                textTransform: "lowercase",
              }}
              disabled={!!userModel.UserId}
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              fullWidth
              id="UserName"
              name="UserName"
              label={localize.username}
              variant="outlined"
              onChange={handleChange}
              value={userModel.UserName}
            />
          </FormControl>
        </div>

        <div className="flex gap-2 w-full">
          {userModel.UserId && (
            <FormControlLabel
              control={<Checkbox onChange={handleUserPasswordCheckedChange} />}
            />
          )}
          <FormControl required fullWidth>
            <TextField
              required
              disabled={!!userModel.UserId && !modifyPassword}
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                ...getInputProps(),
                maxLength: 20,
              }}
              type="password"
              size="small"
              fullWidth
              id="Password"
              name="UserPassword"
              label={localize.password}
              variant="outlined"
              onChange={handleChange}
              value={userModel.Password}
            />
          </FormControl>
        </div>

        <div
          className="flex w-full mb-3"
          style={{ paddingLeft: userModel.UserId > 0 ? "55px" : "0px" }}
        >
          <FormControl required fullWidth>
            <TextField
              required
              disabled={!!userModel.UserId && !modifyPassword}
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                ...getInputProps(),
                maxLength: 20,
              }}
              type="password"
              size="small"
              fullWidth
              id="ConfirmPassword"
              name="ConfirmPassword"
              label={localize.confirm_password}
              variant="outlined"
              onChange={handleChangeConfirmPassword}
              value={userModel.Password}
            />
          </FormControl>
        </div>

        <div className="flex w-full mb-3">
          <Alert
            className="w-full"
            severity={validPassword && passwordConfirmed ? "success" : "error"}
          >
            <label className="text-xs">
              {!validPassword
                ? localize.password_restriction
                : passwordConfirmed
                ? localize.valid_password
                : localize.password_no_match}
            </label>
          </Alert>
        </div>
      </form>
    </div>
  );
}
